<template>
    <div class="renters-tenants-dashboard">
        <!-- Sidebar KPIs -->
        <aside class="kpi-sidebar">
            <h3>{{ $t("dashboards.renters.kpiTitle") }}</h3>
            <ul class="kpi-list">
                <li v-for="kpi in kpis" :key="kpi.id" :class="['kpi-item', { active: kpi.id === activeKpiId }]"
                    @click="setActiveKpi(kpi.id)" @keyup.enter="setActiveKpi(kpi.id)" role="button" tabindex="0"
                    :aria-selected="kpi.id === activeKpiId">
                    <img class="kpi-icon" :src="kpi.id === activeKpiId ? kpi.icon.active : kpi.icon.inactive"
                        :alt="kpi.name + ' icon'" />
                    <span>{{ $t(kpi.name) }}</span>
                </li>
            </ul>
        </aside>

        <!-- Main Dashboard Content -->
        <main class="dashboard-main-content">
            <!-- KPI 1: Booking Utilization Rate -->
            <div v-if="activeKpiId === 1" class="dashboard-view">
                <div class="widgets-row">
                    <div class="widget utilization-level">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.utilizationLevel") }}</h4>
                            <span class="icon-wrap">
                                <img :src="icons.utilization" alt="" />
                            </span>
                        </div>
                        <div class="metric">
                            <span class="value">{{ currentMonthData.bookingUtilization.rate }}%</span>
                        </div>
                        <span class="unit">{{
                            $t("dashboards.renters.bookedVsAvailable")
                        }}</span>
                    </div>

                    <div class="widget change-utilization">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.utilizationChange") }}</h4>
                            <img :src="icons.detailPrediction" class="ghost-icon" alt="" />
                        </div>
                        <div class="metric change-positive" v-if="currentMonthData.bookingUtilization.changePercent">
                            <span class="icon"
                                :style="{ color: currentMonthData.bookingUtilization.changePercent >= 0 ? '#22c55e' : '#ef4444' }">{{
                                    currentMonthData.bookingUtilization.changePercent >= 0 ? '▲' : '▼' }}</span>
                            <span class="value">{{ currentMonthData.bookingUtilization.changePercent >= 0 ? '+' : ''
                            }}{{
                                    currentMonthData.bookingUtilization.changePercent.toFixed(1) }}%</span>
                        </div>
                        <div class="metric" v-else>
                            <span class="value">--</span>
                        </div>
                        <span class="unit">{{
                            $t("dashboards.renters.utilizationComparison")
                        }}</span>
                    </div>

                    <div class="widget peak-usage">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.peakUsageDays") }}</h4>
                            <span class="icon-wrap">
                                <img :src="icons.costPrediction" alt="" />
                            </span>
                        </div>
                        <div class="metric">
                            <span class="unit" style="font-weight: 650">{{
                                currentMonthData.bookingUtilization.peakDays.join(' & ')
                            }}</span>
                        </div>
                    </div>

                    <div class="widget top-spaces">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.topSpaces") }}</h4>
                            <img :src="icons.activity" class="ghost-icon" alt="" />
                        </div>
                        <div class="metric">
                            <div class="space-item"
                                v-for="(space, index) in currentMonthData.bookingUtilization.topSpaces" :key="index">
                                {{ space.name }} ({{ space.utilization }}%)
                            </div>
                        </div>
                    </div>
                </div>
                <div class="usage-estimate">
                    <div class="chart-header-interactive">
                        <h3>{{ $t("dashboards.renters.bookingUtilizationRate") }}</h3>
                        <div class="chart-controls">
                            <button @click="toggleChartType" class="chart-btn">
                                {{
                                    chartType === "bar"
                                        ? $t("dashboards.renters.lineView")
                                        : $t("dashboards.renters.barView")
                                }}
                            </button>
                        </div>
                    </div>
                    <apexchart :options="bookingUtilizationChart.options" :series="bookingUtilizationChart.series"
                        :type="chartType" height="300" />
                </div>
            </div>

            <!-- KPI 2: Energy Usage per Booking -->
            <div v-else-if="activeKpiId === 2" class="dashboard-view carbon-emissions-view">
                <div class="widgets-row">
                    <div class="widget energy-intensity" @click="showKpiDetails('energy-intensity')" :class="{
                        'widget-selected': selectedWidget === 'energy-intensity',
                    }">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.avgEnergyBooking") }}</h4>
                            <img :src="icons.detailPrediction" class="ghost-icon" alt="" />
                        </div>
                        <div class="metric">
                            <span class="value">{{ currentMonthData.energyPerBooking.avgEnergy }}</span>
                            <span class="unit">{{ $t("dashboards.renters.avgEnergyBookingValue") }}</span>
                        </div>
                    </div>

                    <div class="widget utilization-level" @click="showKpiDetails('energy-change')"
                        :class="{ 'widget-selected': selectedWidget === 'energy-change' }">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.changeInEnergyUse") }}</h4>
                            <img :src="icons.energyUse" class="ghost-icon" alt="" />
                        </div>
                        <div class="metric change-positive" v-if="currentMonthData.energyPerBooking.changePercent">
                            <span class="icon"
                                :style="{ color: currentMonthData.energyPerBooking.changePercent >= 0 ? '#ef4444' : '#22c55e' }">{{
                                    currentMonthData.energyPerBooking.changePercent >= 0 ? '▲' : '▼' }}</span>
                            <span class="value">{{ currentMonthData.energyPerBooking.changePercent >= 0 ? '+' : '' }}{{
                                currentMonthData.energyPerBooking.changePercent.toFixed(1) }}%</span>
                        </div>
                        <div class="metric" v-else>
                            <span class="value">--</span>
                        </div>
                        <span class="unit">
                            {{ $t("dashboards.renters.energyComparison") }}</span>
                    </div>

                    <div class="widget peak-usage" @click="showKpiDetails('peak-energy')"
                        :class="{ 'widget-selected': selectedWidget === 'peak-energy' }">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.peakEnergyHours") }}</h4>
                            <span class="icon-wrap">
                                <img :src="icons.costPrediction" alt="" />
                            </span>
                        </div>
                        <div class="metric">
                            <span class="unit" style="font-weight: 600">{{
                                currentMonthData.energyPerBooking.peakHours.join(', ') }}</span>
                        </div>
                    </div>

                    <div class="widget top-spaces" @click="showKpiDetails('top-consumers')"
                        :class="{ 'widget-selected': selectedWidget === 'top-consumers' }">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.topConsumers") }}</h4>
                            <img :src="icons.activity" class="ghost-icon ghost-on-blue" alt="" />
                        </div>
                        <div class="metric">
                            <span class="space-item"
                                v-for="(consumer, index) in currentMonthData.energyPerBooking.topConsumers"
                                :key="index">
                                {{ consumer }}</span>
                        </div>
                    </div>
                </div>

                <div class="main-charts-grid">
                    <div class="chart-card total-emissions" @click="togglePieInteraction">
                        <div class="te-heading">
                            <div class="te-title">
                                <h3>{{ $t("dashboards.renters.totalEnergyUsage") }}</h3>
                            </div>
                        </div>

                        <div class="emissions-breakdown">
                            <div v-for="(item, index) in energyBreakdownData" :key="index"
                                class="emissions-item interactive-legend" @click="toggleFloorData(index)"
                                :class="{ 'legend-disabled': item.disabled }">
                                <span class="floor-indicator" :style="{ backgroundColor: item.color }"></span>
                                <span class="floor-label">{{ item.label }}</span>
                                <span class="percentage">{{ item.percentage }}%</span>
                                <span class="change" :class="item.changeType">({{ item.change >= 0 ? '+' : '' }}{{
                                    item.change }}%)</span>
                            </div>
                        </div>

                        <div class="pie-chart-container">
                            <apexchart :options="totalEnergyChart.options" :series="totalEnergyChart.series" type="pie"
                                height="280" />
                        </div>
                    </div>

                    <div class="chart-card emissions-gauge">
                        <h3>{{ $t("dashboards.renters.benchmarkComparison") }}</h3>
                        <p class="subtitle">(kWh)</p>
                        <div class="gauge-container">
                            <apexchart :options="benchmarkGaugeChart.options" :series="benchmarkGaugeChart.series"
                                type="radialBar" height="280" />
                        </div>
                        <div class="benchmark-label bottom">
                            {{ $t("dashboards.renters.targetEnergy") }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- KPI 3: Comfort Satisfaction Index -->
            <div v-else-if="activeKpiId === 3" class="dashboard-view comfort-satisfaction-view">
                <div class="satisfaction-metrics-row">
                    <div class="satisfaction-metric-card" :class="{ selected: selectedSatisfactionMetric === 'energy' }"
                        @click="selectSatisfactionMetric('energy')">
                        <div class="circular-progress-container">
                            <svg class="circular-progress" viewBox="0 0 120 120">
                                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)"
                                    stroke-width="10" fill="none" />
                                <circle class="progress-circle orange" cx="60" cy="60" r="50"
                                    :stroke-dasharray="circumference" :stroke-dashoffset="animatedProgress ? getProgressOffset(currentMonthData.comfortSatisfaction.energyAnalytics) : circumference
                                        " stroke-width="10" fill="none"
                                    style="transition: stroke-dashoffset 1.5s ease-in-out" />
                            </svg>
                            <div class="progress-center-value">
                                <span class="percentage">{{ currentMonthData.comfortSatisfaction.energyAnalytics
                                }}%</span>
                            </div>
                        </div>
                        <div class="metric-details">
                            <h5>{{ $t("dashboards.renters.energyAnalytics") }}</h5>
                            <p>{{ $t("dashboards.renters.energyEfficiencyNote") }}</p>
                        </div>
                    </div>

                    <div class="satisfaction-metric-card"
                        :class="{ selected: selectedSatisfactionMetric === 'occupancy' }"
                        @click="selectSatisfactionMetric('occupancy')">
                        <div class="circular-progress-container">
                            <svg class="circular-progress" viewBox="0 0 120 120">
                                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)"
                                    stroke-width="10" fill="none" />
                                <circle class="progress-circle blue" cx="60" cy="60" r="50"
                                    :stroke-dasharray="circumference" :stroke-dashoffset="animatedProgress ? getProgressOffset(currentMonthData.comfortSatisfaction.dynamicOccupancy) : circumference
                                        " stroke-width="10" fill="none"
                                    style="transition: stroke-dashoffset 1.5s ease-in-out" />
                            </svg>
                            <div class="progress-center-value">
                                <span class="percentage">{{ currentMonthData.comfortSatisfaction.dynamicOccupancy
                                }}%</span>
                            </div>
                        </div>
                        <div class="metric-details">
                            <h5>{{ $t("dashboards.renters.dynamicOccupancy") }}</h5>
                            <p>{{ $t("dashboards.renters.dynamicOccupancyNote") }}</p>
                        </div>
                    </div>

                    <div class="satisfaction-metric-card"
                        :class="{ selected: selectedSatisfactionMetric === 'utilization' }"
                        @click="selectSatisfactionMetric('utilization')">
                        <div class="circular-progress-container">
                            <svg class="circular-progress" viewBox="0 0 120 120">
                                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)"
                                    stroke-width="10" fill="none" />
                                <circle class="progress-circle green" cx="60" cy="60" r="50"
                                    :stroke-dasharray="circumference" :stroke-dashoffset="animatedProgress ? getProgressOffset(currentMonthData.comfortSatisfaction.utilizationAnalytics) : circumference
                                        " stroke-width="10" fill="none"
                                    style="transition: stroke-dashoffset 1.5s ease-in-out" />
                            </svg>
                            <div class="progress-center-value">
                                <span class="percentage">{{
                                    currentMonthData.comfortSatisfaction.utilizationAnalytics }}%</span>
                            </div>
                        </div>
                        <div class="metric-details">
                            <h5>{{ $t("dashboards.renters.utilizationAnalytics") }}</h5>
                            <p>{{ $t("dashboards.renters.utilizationAnalyticsNote") }}</p>
                        </div>
                    </div>
                </div>

                <div class="comfort-timeline">
                    <div class="chart-section">
                        <div class="chart-header-interactive">
                            <h3>{{ $t("dashboards.renters.emissionsOverTime") }}</h3>
                        </div>
                        <p class="chart-note">
                            {{ $t("dashboards.renters.emissionsEnergyNote") }}
                        </p>

                        <div class="timeline-chart-wrapper">
                            <apexchart :options="comfortEmissionsChart.options" :series="comfortEmissionsChart.series"
                                type="line" height="250" />
                        </div>

                        <div class="timeline-chart-legend">
                            <div class="legend-item">
                                <span class="legend-color-box" style="background: #3b82f6"></span>
                                <span>{{ $t("dashboards.renters.usageByArea") }}</span>
                            </div>
                            <div class="legend-item">
                                <span class="legend-color-box dashed-legend" style="background: #f59e0b"></span>
                                <span>{{
                                    $t("dashboards.renters.secondaryEmissionsNote")
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- KPI 4: Average Booking Duration -->
            <div v-else-if="activeKpiId === 4" class="dashboard-view booking-duration-view">
                <div class="widgets-row">
                    <div class="widget avg-duration">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.avgDuration") }}</h4>
                            <span class="icon-wrap">
                                <img :src="icons.utilization" alt="" />
                            </span>
                        </div>
                        <div class="metric">
                            <span class="value">{{ currentMonthData.bookingDuration.avgDuration }}</span>
                            <span class="unit"> {{ $t("dashboards.renters.hours") }}</span>
                        </div>
                        <span class="unit">{{ $t("dashboards.renters.perBooking") }}</span>
                    </div>

                    <div class="widget total-bookings">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.totalBookings") }}</h4>
                            <img :src="icons.activity" class="ghost-icon" alt="" />
                        </div>
                        <div class="metric">
                            <span class="value">{{ currentMonthData.bookingDuration.totalBookings }}</span>
                        </div>
                        <span class="unit">{{
                            $t("dashboards.renters.bookingsThisWeek")
                        }}</span>
                    </div>

                    <div class="widget duration-efficiency">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.durationEfficiency") }}</h4>
                            <span class="icon-wrap">
                                <img :src="icons.costPrediction" alt="" />
                            </span>
                        </div>
                        <div class="metric change-positive" v-if="currentMonthData.bookingDuration.changePercent">
                            <span class="icon"
                                :style="{ color: currentMonthData.bookingDuration.changePercent >= 0 ? '#22c55e' : '#ef4444' }">{{
                                    currentMonthData.bookingDuration.changePercent >= 0 ? '▲' : '▼' }}</span>
                            <span class="value">{{ currentMonthData.bookingDuration.changePercent >= 0 ? '+' : '' }}{{
                                currentMonthData.bookingDuration.changePercent.toFixed(1) }}%</span>
                        </div>
                        <div class="metric" v-else>
                            <span class="value">--</span>
                        </div>
                        <span class="unit">
                            {{ $t("dashboards.renters.durationComparison") }}</span>
                    </div>

                    <div class="widget peak-booking-hours">
                        <div class="widget-top">
                            <h4>{{ $t("dashboards.renters.peakBookingDaysHours") }}</h4>
                            <img :src="icons.energyUse" class="ghost-icon" alt="" />
                        </div>
                        <div class="metric">
                            <span class="unit" style="font-weight: 600">
                                {{ currentMonthData.bookingDuration.peakBookingDays.join(', ') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Main Charts Grid -->
                <div class="duration-charts-grid">
                    <!-- Duration Distribution -->
                    <div class="chart-card duration-distribution">
                        <div class="chart-header-interactive">
                            <h3>
                                {{ $t("dashboards.renters.bookingDurationDistribution") }}
                            </h3>
                            <div class="chart-controls">
                                <button @click="toggleDurationView" class="chart-btn">
                                    {{
                                        durationViewType === "bar"
                                            ? $t("dashboards.renters.pieView")
                                            : $t("dashboards.renters.barView")
                                    }}
                                </button>
                            </div>
                        </div>
                        <div class="duration-chart">
                            <apexchart :options="durationDistributionChart.options"
                                :series="durationDistributionChart.series" :type="durationViewType"
                                :key="durationViewType" height="300" />
                        </div>
                    </div>

                    <!-- Room Utilization by Duration -->
                    <div class="chart-card room-duration-efficiency">
                        <div class="room-chart-header">
                            <h3>{{ $t("dashboards.renters.roomUtilizationByDuration") }}</h3>
                        </div>
                        <div class="room-duration-content">
                            <div class="room-duration-chart-container">
                                <apexchart :options="roomDurationChart.options" :series="roomDurationChart.series"
                                    type="scatter" height="300" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Weekly Trends -->
                <div class="weekly-trends-section">
                    <div class="chart-header-interactive">
                        <h3>{{ $t("dashboards.renters.shortVsLongTrends") }}</h3>
                        <div class="chart-controls">
                            <div class="custom-dropdown">
                                <select v-model="selectedWeek" @change="updateWeeklyData" class="styled-select">
                                    <option value="thisWeek">
                                        {{ $t("dashboards.renters.thisWeek") }}
                                    </option>
                                    <option value="lastWeek">
                                        {{ $t("dashboards.renters.lastWeek") }}
                                    </option>
                                    <option value="twoWeeksAgo">
                                        {{ $t("dashboards.renters.twoWeeksAgo") }}
                                    </option>
                                    <option value="threeWeeksAgo">
                                        {{ $t("dashboards.renters.threeWeeksAgo") }}
                                    </option>
                                    <option value="fourWeeksAgo">
                                        {{ $t("dashboards.renters.fourWeeksAgo") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="trends-chart-wrapper" :class="{ 'chart-updating': isUpdatingChart }">
                        <apexchart :options="weeklyTrendsChart.options" :series="weeklyTrendsChart.series" type="line"
                            height="250" :key="selectedWeek" />
                    </div>
                    <div class="trends-legend">
                        <div class="legend-item">
                            <span class="legend-color-box" style="background: #3b82f6"></span>
                            <span> {{ $t("dashboards.renters.shortBookings") }}</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color-box" style="background: #f59e0b"></span>
                            <span>{{ $t("dashboards.renters.longBookings") }}</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color-box dashed-legend" style="background: #22c55e"></span>
                            <span>{{ $t("dashboards.renters.averageDuration") }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- KPI 5: CO₂ Awareness Index -->
            <div v-else-if="activeKpiId === 5" class="dashboard-view operational-cost-view">
                <div class="cost-metrics-row">
                    <div class="satisfaction-metric-card" @click="selectCostMetric('efficiency')"
                        :class="{ selected: selectedCostMetric === 'efficiency' }">
                        <div class="circular-progress-container">
                            <svg class="circular-progress" viewBox="0 0 120 120">
                                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)"
                                    stroke-width="10" fill="none" />
                                <circle class="progress-circle orange" cx="60" cy="60" r="50"
                                    :stroke-dasharray="circumference" :stroke-dashoffset="animatedProgress ? getProgressOffset(currentMonthData.co2Awareness.energyEfficiency) : circumference
                                        " stroke-width="10" fill="none"
                                    style="transition: stroke-dashoffset 1.5s ease-in-out" />
                            </svg>
                            <div class="progress-center-value">
                                <span class="percentage">{{ currentMonthData.co2Awareness.energyEfficiency }}%</span>
                            </div>
                        </div>
                        <div class="metric-details">
                            <h5>{{ $t("dashboards.renters.energyEfficiency") }}</h5>
                            <p>{{ $t("dashboards.renters.energyEfficiencyNote2") }}</p>
                        </div>
                    </div>

                    <div class="satisfaction-metric-card" @click="selectCostMetric('waste')"
                        :class="{ selected: selectedCostMetric === 'waste' }">
                        <div class="circular-progress-container">
                            <svg class="circular-progress" viewBox="0 0 120 120">
                                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)"
                                    stroke-width="10" fill="none" />
                                <circle class="progress-circle blue" cx="60" cy="60" r="50"
                                    :stroke-dasharray="circumference" :stroke-dashoffset="animatedProgress ? getProgressOffset(currentMonthData.co2Awareness.wasteDiversion) : circumference
                                        " stroke-width="10" fill="none"
                                    style="transition: stroke-dashoffset 1.5s ease-in-out" />
                            </svg>
                            <div class="progress-center-value">
                                <span class="percentage">{{ currentMonthData.co2Awareness.wasteDiversion }}%</span>
                            </div>
                        </div>
                        <div class="metric-details">
                            <h5>{{ $t("dashboards.renters.wasteDiversion") }}</h5>
                            <p>{{ $t("dashboards.renters.wasteDiversionNote") }}</p>
                        </div>
                    </div>

                    <div class="satisfaction-metric-card" @click="selectCostMetric('water')"
                        :class="{ selected: selectedCostMetric === 'water' }">
                        <div class="circular-progress-container">
                            <svg class="circular-progress" viewBox="0 0 120 120">
                                <circle class="bg-circle" cx="60" cy="60" r="50" stroke="rgba(55, 65, 81, 0.3)"
                                    stroke-width="10" fill="none" />
                                <circle class="progress-circle green" cx="60" cy="60" r="50"
                                    :stroke-dasharray="circumference" :stroke-dashoffset="animatedProgress ? getProgressOffset(currentMonthData.co2Awareness.waterConservation) : circumference
                                        " stroke-width="10" fill="none"
                                    style="transition: stroke-dashoffset 1.5s ease-in-out" />
                            </svg>
                            <div class="progress-center-value">
                                <span class="percentage">{{ currentMonthData.co2Awareness.waterConservation }}%</span>
                            </div>
                        </div>
                        <div class="metric-details">
                            <h5>{{ $t("dashboards.renters.waterConservation") }}</h5>
                            <p>{{ $t("dashboards.renters.waterConservationNote") }}</p>
                        </div>
                    </div>
                </div>

                <div class="cost-analysis-row">
                    <div class="chart-card space-efficiency">
                        <div class="card-header">
                            <h3>{{ $t("dashboards.renters.carbonScore") }}</h3>
                        </div>
                        <div class="carbon-score-container">
                            <div class="carbon-donut">
                                <apexchart :options="carbonScoreChart.options" :series="carbonScoreChart.series"
                                    type="donut" height="270" />
                            </div>
                            <div class="carbon-legend">
                                <div class="legend-item" @click="toggleCarbonSegment(0)"
                                    :class="{ 'legend-disabled': carbonDisabled[0] }">
                                    <span class="indicator current-score"></span>
                                    <span> {{ $t("dashboards.renters.currentScore") }}</span>
                                </div>
                                <div class="legend-item" @click="toggleCarbonSegment(1)"
                                    :class="{ 'legend-disabled': carbonDisabled[1] }">
                                    <span class="indicator positive-score"></span>
                                    <span>{{ $t("dashboards.renters.positiveScore") }}</span>
                                </div>
                                <div class="legend-item" @click="toggleCarbonSegment(2)"
                                    :class="{ 'legend-disabled': carbonDisabled[2] }">
                                    <span class="indicator warning"></span>
                                    <span>{{ $t("dashboards.renters.warningScore") }} </span>
                                </div>
                                <div class="legend-item" @click="toggleCarbonSegment(3)"
                                    :class="{ 'legend-disabled': carbonDisabled[3] }">
                                    <span class="indicator benchmark"></span>
                                    <span> {{ $t("dashboards.renters.benchmarkScore") }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="chart-card co2-trends">
                        <h3>{{ $t("dashboards.renters.monthlyTrends") }}</h3>
                        <p class="subtitle">
                            {{ $t("dashboards.renters.estimatedEmissions") }}
                        </p>
                        <div class="timeline-chart">
                            <apexchart :options="co2TimelineChart.options" :series="co2TimelineChart.series" type="line"
                                height="350" />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getRentersTenantsDashboardData } from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/rentersTenantsDashboardData";

/* Icons */
import energy from "@/assets/client-segments/energy-intensity.svg";
import utilization from "@/assets/client-segments/utilization-level.svg";
import detailPrediction from "@/assets/client-segments/detail-cost-prediction.svg";
import activity from "@/assets/client-segments/detail-activity-appliances.svg";
import costPrediction from "@/assets/client-segments/cost-prediction.svg";
import energyUse from "@/assets/client-segments/green-line.svg";

/* KPI icon set (active/inactive) */
import carbonDioxideActive from "@/assets/client-segments/kpis-icons/carbon-dioxide-active.svg";
import carbonDioxideInactive from "@/assets/client-segments/kpis-icons/carbon-dioxide-inactive.svg";
import calendarSearchActive from "@/assets/client-segments/kpis-icons/calendar-search-active.svg";
import calendarSearchInactive from "@/assets/client-segments/kpis-icons/calendar-search-inactive.svg";
import clockActive from "@/assets/client-segments/kpis-icons/clock-active.svg";
import clockInactive from "@/assets/client-segments/kpis-icons/clock-inactive.svg";
import energyConsumptionActive from "@/assets/client-segments/kpis-icons/energy-consumption-active.svg";
import energyConsumptionInactive from "@/assets/client-segments/kpis-icons/energy-consumption-inactive.svg";
import leafActive from "@/assets/client-segments/kpis-icons/leaf-active.svg";
import leafInactive from "@/assets/client-segments/kpis-icons/leaf-inactive.svg";

const icons = {
    energy,
    utilization,
    activity,
    detailPrediction,
    costPrediction,
    energyUse,
};

const { t } = useI18n();

// Load dashboard data
const dashboardData = getRentersTenantsDashboardData();
const currentMonthData = computed(() => dashboardData.getCurrentMonthData());

// Main state
const activeKpiId = ref(1);
const circumference = 2 * Math.PI * 50;
const selectedWidget = ref(null);
const chartType = ref("bar");

// Interactive states
const selectedSatisfactionMetric = ref(null);
const selectedCostMetric = ref(null);
const animatedProgress = ref(false);
const selectedWeek = ref("thisWeek");
const isUpdatingChart = ref(false);

// Carbon chart interactive state
const carbonDisabled = reactive([false, false, false, false]);

// Duration view type
const durationViewType = ref("bar");

// KPI definitions
const kpis = ref([
    {
        id: 1,
        name: "dashboards.renters.utilizationChange",
        icon: {
            active: calendarSearchActive,
            inactive: calendarSearchInactive,
        },
    },
    {
        id: 2,
        name: "dashboards.renters.energyUsagePerBooking",
        icon: {
            active: energyConsumptionActive,
            inactive: energyConsumptionInactive,
        },
    },
    {
        id: 3,
        name: "dashboards.renters.comfortSatisfactionIndex",
        icon: { active: leafActive, inactive: leafInactive },
    },
    {
        id: 4,
        name: "dashboards.renters.avgBookingDuration",
        icon: { active: clockActive, inactive: clockInactive },
    },
    {
        id: 5,
        name: "dashboards.renters.co2AwarenessIndex",
        icon: { active: carbonDioxideActive, inactive: carbonDioxideInactive },
    },
]);

// Energy breakdown data for interactive legend
const energyBreakdownData = computed(() => {
    return currentMonthData.value.energyPerBooking.floorEnergyDistribution.map((item) => ({
        label: item.floor,
        percentage: item.percentage,
        change: item.change,
        changeType: item.change >= 0 ? "positive" : "negative",
        color: item.color,
        disabled: false,
    }));
});

function setActiveKpi(kpiId) {
    activeKpiId.value = kpiId;
    selectedWidget.value = null;
}

// Interactive functions
function showKpiDetails(widget) {
    selectedWidget.value = selectedWidget.value === widget ? null : widget;
}

function toggleChartType() {
    chartType.value = chartType.value === "bar" ? "line" : "bar";
}

function togglePieInteraction() {
    console.log("Pie chart interaction toggled");
}

function toggleFloorData(index) {
    energyBreakdownData.value[index].disabled = !energyBreakdownData.value[index].disabled;
    updatePieChart();
}

function updatePieChart() {
    const activeData = energyBreakdownData.value.filter((item) => !item.disabled);
    totalEnergyChart.value.series = activeData.map((item) => item.percentage);
}

function selectSatisfactionMetric(metric) {
    selectedSatisfactionMetric.value =
        selectedSatisfactionMetric.value === metric ? null : metric;
}

function selectCostMetric(metric) {
    selectedCostMetric.value =
        selectedCostMetric.value === metric ? null : metric;
}

function toggleCarbonSegment(index) {
    carbonDisabled[index] = !carbonDisabled[index];
    updateCarbonChart();
}

function toggleDurationView() {
    durationViewType.value = durationViewType.value === "bar" ? "pie" : "bar";
}

function updateWeeklyData() {
    isUpdatingChart.value = true;
    setTimeout(() => {
        isUpdatingChart.value = false;
    }, 300);
}

function getProgressOffset(percentage) {
    return circumference - (percentage / 100) * circumference;
}

// Chart configurations
const bookingUtilizationChart = computed(() => ({
    series: [
        {
            name: t("dashboards.renters.UtilizationRate"),
            data: currentMonthData.value.bookingUtilization.hourlyUtilization.map((h) => h.rate),
        },
    ],
    options: {
        chart: {
            type: "bar",
            toolbar: { show: false },
            background: "transparent",
            height: 300,
            animations: {
                enabled: true,
                easing: "easeinout",
                speed: 1000,
            },
        },
        colors: ["#10b981"],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "60%",
                borderRadius: 4,
            },
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: currentMonthData.value.bookingUtilization.hourlyUtilization.map((h) => h.hour),
            labels: {
                style: {
                    colors: "#94a3b8",
                    fontSize: "12px",
                },
                rotate: -45,
                rotateAlways: false,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            max: 100,
            min: 0,
            tickAmount: 5,
            labels: {
                style: {
                    colors: "#94a3b8",
                    fontSize: "12px",
                },
                formatter: function (val) {
                    return val + "%";
                },
            },
        },
        grid: {
            borderColor: "#374151",
            strokeDashArray: 1,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        legend: { show: false },
        tooltip: {
            enabled: true,
            theme: "dark",
            y: {
                formatter: function (val) {
                    return val + "%";
                },
            },
        },
    },
}));

const totalEnergyChart = computed(() => ({
    series: energyBreakdownData.value.map((item) => item.percentage),
    options: {
        chart: {
            type: "pie",
            background: "transparent",
            toolbar: { show: false },
        },
        colors: energyBreakdownData.value.map((item) => item.color),
        labels: energyBreakdownData.value.map((item) => item.label),
        legend: { show: false },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: "13px",
                fontWeight: 700,
                colors: ["#ffffff"],
            },
            formatter: (val) => `${Math.round(val)}%`,
        },
    },
}));

const benchmarkGaugeChart = computed(() => ({
    series: [currentMonthData.value.energyPerBooking.benchmarkComparison],
    options: {
        chart: {
            type: "radialBar",
            background: "transparent",
            toolbar: { show: false },
        },
        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: {
                    size: "55%",
                    background: "transparent",
                },
                dataLabels: {
                    show: true,
                    value: {
                        color: "#ffffff",
                        fontSize: "42px",
                        fontWeight: 700,
                        formatter: function (val) {
                            return Math.round(val) + "%";
                        },
                    },
                },
            },
        },
        colors: ["#ef4444"],
        labels: [t("dashboards.renters.emissionsLabel")],
    },
}));

const comfortEmissionsChart = computed(() => {
    // Get last 12 months of data
    const last12Months = dashboardData.monthlyData.slice(-12);

    return {
        series: [
            {
                name: t("dashboards.renters.usageByArea"),
                data: last12Months.map((m) => m.comfortSatisfaction.monthlyTrend.energyUse),
            },
            {
                name: t("dashboards.renters.secondaryEmissionsNote"),
                data: last12Months.map((m) => m.comfortSatisfaction.monthlyTrend.co2Emissions),
            },
        ],
        options: {
            chart: {
                type: "line",
                toolbar: { show: false },
                background: "transparent",
                animations: {
                    enabled: true,
                    easing: "easeinout",
                    speed: 1000,
                },
            },
            colors: ["#3b82f6", "#f59e0b"],
            stroke: {
                curve: "smooth",
                width: [3, 2],
                dashArray: [0, 8],
            },
            markers: {
                size: [5, 4],
                strokeColors: ["#ffffff", "#ffffff"],
                strokeWidth: 2,
                hover: { size: 8 },
            },
            grid: {
                borderColor: "#374151",
                strokeDashArray: 3,
                show: true,
            },
            xaxis: {
                categories: [
                    t("dashboards.renters.jan"),
                    t("dashboards.renters.feb"),
                    t("dashboards.renters.mar"),
                    t("dashboards.renters.apr"),
                    t("dashboards.renters.may"),
                    t("dashboards.renters.jun"),
                    t("dashboards.renters.jul"),
                    t("dashboards.renters.aug"),
                    t("dashboards.renters.sep"),
                    t("dashboards.renters.oct"),
                    t("dashboards.renters.nov"),
                    t("dashboards.renters.dec"),
                ],
                labels: {
                    style: {
                        colors: "#94a3b8",
                        fontSize: "12px",
                    },
                },
            },
            yaxis: [
                {
                    title: {
                        text: t("dashboards.renters.energyUseUnit"),
                        style: { color: "#3b82f6" },
                    },
                    min: 0,
                    max: 1600,
                    labels: {
                        style: {
                            colors: "#94a3b8",
                            fontSize: "12px",
                        },
                    },
                },
                {
                    opposite: true,
                    title: {
                        text: t("dashboards.renters.co2Unit"),
                        style: { color: "#f59e0b" },
                    },
                    min: 0,
                    max: 480,
                    labels: {
                        style: { colors: "#94a3b8", fontSize: "12px" },
                    },
                },
            ],
            legend: { show: false },
            dataLabels: { enabled: false },
            tooltip: {
                theme: "dark",
                shared: true,
                intersect: false,
                y: {
                    formatter: (val, opts) => {
                        return opts.seriesIndex === 0 ? `${val} kWh` : `${val} kg CO₂`;
                    },
                },
            },
        },
    };
});

const durationDistributionChart = computed(() => {
    const isBarChart = durationViewType.value === "bar";
    const dist = currentMonthData.value.bookingDuration.durationDistribution;

    const baseOptions = {
        chart: {
            type: durationViewType.value,
            toolbar: { show: false },
            background: "transparent",
            animations: { enabled: true, easing: "easeinout", speed: 800 },
        },
        colors: ["#2D5BFF", "#388BFF", "#22c55e", "#93AAFD", "#C6D2FD"],
        labels: ["< 1hr", "1-2hrs", "2-4hrs", "4-6hrs", "6+ hrs"],
        plotOptions: {
            bar: { horizontal: false, columnWidth: "60%", borderRadius: 4 },
            pie: { donut: { size: "50%" } },
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return isBarChart ? val : Math.round(val) + "%";
            },
        },
        legend: { show: !isBarChart },
        tooltip: { theme: "dark" },
    };

    if (isBarChart) {
        baseOptions.xaxis = {
            categories: ["< 1hr", "1-2hrs", "2-4hrs", "4-6hrs", "6+ hrs"],
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        };
        baseOptions.yaxis = {
            labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
        };
        baseOptions.grid = {
            borderColor: "#374151",
            strokeDashArray: 1,
        };
    }

    return {
        series: isBarChart
            ? [
                {
                    name: t("dashboards.renters.bookings"),
                    data: [
                        dist.lessThan1Hr,
                        dist.oneToTwoHrs,
                        dist.twoToFourHrs,
                        dist.fourToSixHrs,
                        dist.moreThanSixHrs,
                    ],
                },
            ]
            : [
                dist.lessThan1Hr,
                dist.oneToTwoHrs,
                dist.twoToFourHrs,
                dist.fourToSixHrs,
                dist.moreThanSixHrs,
            ],
        options: baseOptions,
    };
});

const roomDurationChart = computed(() => {
    const roomData = currentMonthData.value.bookingDuration.roomEfficiency;

    return {
        series: [
            {
                name: t("dashboards.renters.roomEfficiency"),
                data: roomData.map((room) => [
                    room.avgDuration,
                    room.efficiency,
                    room.bookings,
                ]),
            },
        ],
        options: {
            chart: {
                type: "scatter",
                toolbar: { show: false },
                background: "transparent",
            },
            colors: ["#22c55e"],
            xaxis: {
                title: {
                    text: t("dashboards.renters.averageDurationHrs"),
                    style: { color: "#94a3b8" },
                },
                labels: {
                    style: { colors: "#94a3b8" },
                },
            },
            yaxis: {
                title: {
                    text: t("dashboards.renters.efficiency"),
                    style: { color: "#94a3b8" },
                },
                labels: {
                    style: { colors: "#94a3b8" },
                },
            },
            markers: {
                size: 8,
                hover: { size: 10 },
            },
            grid: {
                borderColor: "#374151",
                strokeDashArray: 1,
            },
            tooltip: {
                theme: "dark",
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const data = w.config.series[seriesIndex].data[dataPointIndex];
                    const room = roomData[dataPointIndex];
                    return `<div class="tooltip-content" style="padding: 8px; background: #1f2937; border-radius: 4px;">
            <strong>${room.room}</strong><br>
            Duration: ${data[0]}hrs<br>
            Efficiency: ${data[1]}%<br>
            Bookings: ${data[2]}
          </div>`;
                },
            },
        },
    };
});

const weeklyTrendsChart = computed(() => {
    const trends = currentMonthData.value.bookingDuration.weeklyTrends;

    return {
        series: [
            {
                name: t("dashboards.renters.shortBookings"),
                data: trends.shortBookings,
            },
            {
                name: t("dashboards.renters.longBookings"),
                data: trends.longBookings,
            },
            {
                name: t("dashboards.renters.avgDuration"),
                data: trends.avgDuration,
            },
        ],
        options: {
            chart: {
                type: "line",
                toolbar: { show: false },
                background: "transparent",
            },
            colors: ["#3b82f6", "#f59e0b", "#22c55e"],
            stroke: {
                curve: "smooth",
                width: [3, 3, 2],
                dashArray: [0, 0, 8],
            },
            markers: {
                size: [5, 5, 4],
                hover: { size: 8 },
            },
            xaxis: {
                categories: ["Mon", "Tue", "Wed", "Thu", "Fri"],
                labels: {
                    style: { colors: "#94a3b8" },
                },
            },
            yaxis: [
                {
                    title: {
                        text: t("dashboards.renters.numberOfBookings"),
                        style: { color: "#94a3b8" },
                    },
                    labels: {
                        style: { colors: "#94a3b8" },
                    },
                },
                {
                    opposite: true,
                    title: {
                        text: t("dashboards.renters.averageDurationHrs"),
                        style: { color: "#94a3b8" },
                    },
                    labels: {
                        style: { colors: "#94a3b8" },
                    },
                },
            ],
            grid: {
                borderColor: "#374151",
                strokeDashArray: 1,
            },
            legend: { show: false },
            tooltip: {
                theme: "dark",
                shared: true,
            },
        },
    };
});

const carbonScoreChart = computed(() => {
    const carbonData = currentMonthData.value.co2Awareness.carbonScore;

    return {
        series: [
            carbonData.currentScore,
            carbonData.positiveActions,
            carbonData.warningZone,
            carbonData.benchmark,
        ],
        options: {
            responsive: [
                {
                    breakpoint: 768,
                    options: {
                        chart: { height: 180 },
                        plotOptions: {
                            pie: {
                                donut: {
                                    size: "60%",
                                    labels: { total: { fontSize: "24px" } },
                                },
                            },
                        },
                    },
                },
            ],
            chart: {
                type: "donut",
                background: "transparent",
                height: 180,
                toolbar: { show: false },
                animations: {
                    enabled: true,
                    easing: "easeinout",
                    speed: 800,
                },
            },
            colors: ["#3b82f6", "#22c55e", "#f59e0b", "#6b7280"],
            labels: [
                t("dashboards.renters.currentScore"),
                t("dashboards.renters.positiveScore"),
                t("dashboards.renters.warningScore"),
                t("dashboards.renters.benchmarkScore"),
            ],
            legend: { show: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: "65%",
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                showAlways: true,
                                label: "",
                                fontSize: "28px",
                                fontWeight: "bold",
                                color: "#ffffff",
                                formatter: function () {
                                    return currentMonthData.value.co2Awareness.overallScore + "%";
                                },
                            },
                        },
                    },
                    expandOnClick: true,
                },
            },
            dataLabels: { enabled: false },
            tooltip: {
                enabled: true,
                theme: "dark",
            },
        },
    };
});

const co2TimelineChart = computed(() => {
    const last12Months = dashboardData.monthlyData.slice(-12);

    return {
        series: [
            {
                name: t("dashboards.renters.co2Emissions"),
                data: last12Months.map((m) => m.co2Awareness.monthlyEmissions[11]),
            },
        ],
        options: {
            chart: {
                type: "line",
                toolbar: { show: false },
                background: "transparent",
                animations: {
                    enabled: true,
                    easing: "easeinout",
                    speed: 700,
                },
            },
            colors: ["#22c55e"],
            stroke: {
                curve: "smooth",
                width: 3,
                lineCap: "round",
            },
            markers: {
                size: 6,
                hover: {
                    size: 8,
                },
            },
            grid: {
                borderColor: "#374151",
                strokeDashArray: 1,
            },
            xaxis: {
                categories: [
                    t("dashboards.renters.jan"),
                    t("dashboards.renters.feb"),
                    t("dashboards.renters.mar"),
                    t("dashboards.renters.apr"),
                    t("dashboards.renters.may"),
                    t("dashboards.renters.jun"),
                    t("dashboards.renters.jul"),
                    t("dashboards.renters.aug"),
                    t("dashboards.renters.sep"),
                    t("dashboards.renters.oct"),
                    t("dashboards.renters.nov"),
                    t("dashboards.renters.dec"),
                ],
                labels: {
                    style: {
                        colors: "#8b92a9",
                        fontSize: "14px",
                    },
                },
            },
            yaxis: {
                min: 0,
                max: 400,
                tickAmount: 8,
                labels: {
                    style: {
                        colors: "#8b92a9",
                        fontSize: "14px",
                    },
                },
            },
            legend: { show: false },
            dataLabels: { enabled: false },
            tooltip: {
                theme: "dark",
                x: { show: true },
                y: {
                    formatter: function (val) {
                        return val + " kg CO2";
                    },
                },
            },
        },
    };
});

function updateCarbonChart() {
    const carbonData = currentMonthData.value.co2Awareness.carbonScore;
    const activeSegments = [
        carbonData.currentScore,
        carbonData.positiveActions,
        carbonData.warningZone,
        carbonData.benchmark,
    ].map((value, index) => (carbonDisabled[index] ? 0 : value));

    carbonScoreChart.value.series = activeSegments;
}

onMounted(() => {
    setTimeout(() => {
        animatedProgress.value = true;
    }, 300);
});

// Watch for KPI changes to reset animations
watch(activeKpiId, () => {
    animatedProgress.value = false;
    setTimeout(() => {
        animatedProgress.value = true;
    }, 100);
});
</script>

<style scoped>
@import "@/components/OneVrAnalytics/AnalyticsDashboardStyles/RentersTenants.css";
</style>